import { MDXProvider } from "@mdx-js/react"
import { Link } from "gatsby"
import { convertSlugToCategoryName, getCategorySlug } from "libs/utils/dom.ts"
import React from "react"
import styled from "styled-components"
import { Col, Container, Row } from "styleguide/components/grid"
import MdxHr from "styleguide/components/MdxHr"
import { H1 } from "styleguide/components/Text"
import P from "styleguide/components/Text/P"
import { IconDot } from "styleguide/icons"
import { uiColorToBaseColor } from "styleguide/styles/colors"
import media from "styleguide/styles/media-queries"
import { ANCHOR_ICON_WIDTH } from "styleguide/styles/constants"
import { sizeToCss } from "../../components/Text/sizes"
import ArticleAuthor from "./ArticleAuthor"
import ClosingRemark from "./ClosingRemark"

const shortcodes = {
  p: P,
  MdxHr,
}

interface Props {
  body: any
  title: string
  author?: string
  date?: string
  link: string
  categories: string[]
}

const StyledH1 = styled(H1)`
  margin: 0;
  position: relative;
  font-size: 36px;
  line-height: 48px;

  ${media.tablet`
    font-size: 26px;
    line-height: 32px;
  `}
`

const StyledContainer = styled(Container)`
  margin-bottom: 112px;

  h1,
  h2,
  h3,
  h4 {
    padding-left: ${ANCHOR_ICON_WIDTH};
    left: -${ANCHOR_ICON_WIDTH};
    width: 100%;
  }

  h2 {
    margin: 64px 0 24px 0;
    font-size: 26px;
    line-height: 32px;

    svg {
      margin-right: 7px;
    }

    ${media.tablet`
      font-size: 22px;
      line-height: 32px;
    `}
  }

  h3 {
    margin: 40px 0 24px 0;
    font-size: 22px;
    line-height: 32px;

    svg {
      margin-right: 7px;
    }

    ${media.tablet`
      font-size: 18px;
      line-height: 24px;
    `}
  }

  ol li {
    margin: 16px 0;

    a {
      word-wrap: break-word;
    }
  }

  figure {
    margin: 0 0 40px 0;

    span {
      border-radius: 8px;
    }

    figcaption {
      text-align: center;
      margin-top: 16px;
      color: ${uiColorToBaseColor("darkestGray")};

      ${() => sizeToCss("L")}
    }
  }

  iframe {
    width: 680px;
    height: 382px;
    margin-top: 50px;

    ${media.tablet`
    width: 100%;
    height: auto;
   `}
  }

  .emphasizedText {
    font-size: 30px;
    line-height: 44px;
    color: #000;
    opacity: 0.5;
  }

  .flex1 {
    flex: 1;
  }

  .displayFlex {
    display: flex;

    ${media.tablet`
      flex-direction: column;
   `}
  }

  blockquote {
    font-style: italic;
    position: relative;
    margin-left: 0;

    p {
      padding-left: 24px;
    }
  }

  blockquote:before {
    content: "";
    border-left: 2px solid ${uiColorToBaseColor("lightestGray")};
    border-right: 2px solid ${uiColorToBaseColor("lightestGray")};
    border-radius: 2px;
    height: 100%;
    position: absolute;
    left: 0;
    top: 0;
  }
`

const MetaContainer = styled.div`
  display: flex;
  align-items: center;
  margin-top: 24px;
  flex-wrap: wrap;

  ${media.desktop`
      margin-bottom: 16px;
   `}

  svg {
    width: 3px;
    height: 3px;
    margin: 0 8px;

    ${media.desktop`
      margin-bottom: 8px;
   `}
  }

  span {
    color: ${uiColorToBaseColor("gray")};
    font-size: 14px;
    line-height: 24px;

    ${media.desktop`
      margin-bottom: 8px;
   `}
  }
`

const ContentContainer = styled.div`
  display: block;
  margin-top: 24px;
`

const AuthorContainer = styled.div`
  display: block;
  margin-top: 80px;
`

const Category = styled(Link)`
  text-transform: capitalize;
  font-size: 14px;
  padding: 4px 12px;
  line-height: 24px;
  border: 1px solid ${uiColorToBaseColor("lightGray")};
  border-radius: 4px;
  margin-right: 8px;

  &:last-of-type {
    margin-right: 24px;
  }

  ${media.desktop`
      margin-bottom: 8px;
   `}
`

const ArticleContent = ({
  title,
  author,
  date,
  body,
  link,
  categories,
}: Props) => (
  <StyledContainer>
    <Row>
      <Col md={8} offset={{ md: 2 }}>
        <StyledH1>{title}</StyledH1>
        <MetaContainer>
          {categories.map((category) => (
            <Category
              key={`category-label-${category}`}
              to={getCategorySlug(category)}
            >
              {convertSlugToCategoryName(category)}
            </Category>
          ))}
          {date && (
            <>
              <span>{date}</span>
              <IconDot />
            </>
          )}
          {author && <span>by {author}</span>}
        </MetaContainer>
        <ContentContainer>
          <MDXProvider components={shortcodes}>
            {body}
          </MDXProvider>
        </ContentContainer>
        <ClosingRemark />
        <AuthorContainer>
          <ArticleAuthor author={author} link={link} />
        </AuthorContainer>
      </Col>
    </Row>
  </StyledContainer>
)

export default ArticleContent
